import {useNavigate} from 'react-router-dom';
import {GuidesEnum, MyAppointmentsEnum} from 'presentation/constants';
import {useContractStatus} from 'presentation/hooks';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import {useOpenOptionsDrawerStore} from 'presentation/pages/private/store/useOpenOptionsDrawer';

import {OptionsDrawerItem} from '../types';
import {translations} from 'presentation/translations-new';
import {usePpoNetworkStore} from 'presentation/modules/guide/shared/store/guideStore/usePpoNetworkStore';
import {ppoNetworkTypeEnum} from 'presentation/modules/guide/shared/store/guideStore/types';
import {BookingTypeEnum} from 'presentation/pages/private/booking/constants';
import {useBookingConfirmationDrawer} from 'presentation/pages/private/booking/hooks/useBookingConfirmationDrawer';
import {useOverbookingModalStore} from 'presentation/pages/private/booking/components/Overbooking/store';

const {bookExams, bookOdonto, bookHealth, consultHealthNetwork, myAppointments} =
	translations['pt-br'].privateFlow.services;

function useNotredameNetworkItems() {
	const navigate = useNavigate();
	const closeOptionsDrawer = useOpenOptionsDrawerStore(state => state.closeOptionsDrawer);
	const setPpoNetworkType = usePpoNetworkStore(state => state.setPpoNetworkType);
	const {setSelectedServiceType, handleOpenBookingConfirmationDrawer} =
		useBookingConfirmationDrawer();
	const {
		flPlanoSomenteHospitalar,
		healthOnly,
		odontoOnly,
		healthOdonto,
		isResponsibleWithoutBenefits,
		isEarlyAccess,
		isB2BAccess,
	} = useAccountInformation();
	const {isValid, isContractSuspended, isCancelledLessThanThreeMonths} = useContractStatus();
	const setOverbookingModalOpen = useOverbookingModalStore(
		state => state.setOverbookingModalOpen,
	);
	const showBookService =
		!isResponsibleWithoutBenefits &&
		(healthOdonto || (healthOnly && !flPlanoSomenteHospitalar));

	const showHealthBookService =
		!isResponsibleWithoutBenefits &&
		!isContractSuspended &&
		((healthOnly && !flPlanoSomenteHospitalar) || (healthOdonto && !flPlanoSomenteHospitalar));

	const showOdontoBookService =
		!isResponsibleWithoutBenefits && !isContractSuspended && (odontoOnly || healthOdonto);

	const showBookConsulta = isValid && showHealthBookService && !isEarlyAccess;
	const showBookExame = isValid && !isContractSuspended && showBookService && !isEarlyAccess;
	const showBookDentista = isValid && showOdontoBookService && !isEarlyAccess;

	const showMyAppointments =
		(isValid && !isResponsibleWithoutBenefits && !(healthOnly && flPlanoSomenteHospitalar)) ||
		(isCancelledLessThanThreeMonths && !isResponsibleWithoutBenefits);

	const showNetworkProviderHealth =
		isValid && !isB2BAccess && !isResponsibleWithoutBenefits && !odontoOnly;

	const handleMyAppointmentsClick = () => {
		closeOptionsDrawer();
		navigate(MyAppointmentsEnum.MY_APPOINTMENTS);
	};

	const handleClickNetwork = (route: string) => {
		closeOptionsDrawer();
		navigate(route);
	};

	const handleBookServiceClick = (bookingType: BookingTypeEnum) => {
		closeOptionsDrawer();

		if (bookingType === BookingTypeEnum.HEALTH) {
			setOverbookingModalOpen(true);
			return;
		}

		setSelectedServiceType(bookingType);
		handleOpenBookingConfirmationDrawer();
	};

	const ALL_ITEMS: (OptionsDrawerItem & {show: boolean})[] = [
		{
			label: bookHealth,
			icon: 'stethoscope-default',
			onClick: () => handleBookServiceClick(BookingTypeEnum.HEALTH),
			show: showBookConsulta,
		},
		{
			label: bookExams,
			icon: 'needle',
			onClick: () => handleBookServiceClick(BookingTypeEnum.EXAM),
			show: showBookExame,
		},
		{
			label: bookOdonto,
			icon: 'tooth-outline',
			onClick: () => handleBookServiceClick(BookingTypeEnum.ODONTO),
			show: showBookDentista,
		},
		{
			label: consultHealthNetwork,
			icon: 'stethoscope',
			onClick: () => {
				setPpoNetworkType(ppoNetworkTypeEnum.SELF_NETWORK);
				handleClickNetwork(GuidesEnum.SERVICE_NETWORK_HEALTH);
			},
			show: showNetworkProviderHealth,
		},
		{
			label: myAppointments,
			icon: 'event-filled',
			onClick: handleMyAppointmentsClick,
			show: showMyAppointments,
		},
	];

	const items: OptionsDrawerItem[] = ALL_ITEMS.filter(item => item.show).map(
		({show: _, ...item}) => item,
	);

	return {items};
}

export {useNotredameNetworkItems};
